import { useQueryParams } from '@calendly/hooks'
import { I18n } from '@calendly/i18n'
import { Button } from '@calendly/ui/components/button'
import { useDropdown } from '@calendly/ui/components/dropdown'
import { AddMiniIcon, ArrowChevronDownMiniIcon, ArrowChevronUpMiniIcon } from '@calendly/ui/components/icons'
import { useMainContainerSize } from '@calendly/ui/hooks/main-container-size-context'
import { useFloating } from '@calendly/ui/hooks/use-floating'
import { useCallback, useState } from 'react'

import { useGetUserQuery } from 'common'
import { EventTypeSelectPopup } from 'components/event_type_select_popup/event_type_select_popup'
import { UserEventTypeCreationDropdown } from 'features/event_type_creation_dropdown'
import { getSingleUseLinkEventTypeAttrs, useSingleUseLinkSidebar } from 'features/single_use_link_sidebar'
import { useScope } from 'hooks/meetings/use_scope'

import { ButtonText, ButtonWrapper, Menu } from './styles'

export const CreateButton = ({ dropdownOnRight = false, buttonLabel = null, canOpenByDefault = true }) => {
  const { new: newET } = useQueryParams()
  const isOpenByDefault = canOpenByDefault && Boolean(newET)
  const [toggle, Dropdown, isOpen, { dropdownId }] = useDropdown(isOpenByDefault, true)
  const [setReference, FloatingWrapper] = useFloating(isOpen, { placement: 'bottom-start' })
  const { isLargeMainContainer } = useMainContainerSize()
  const { data: { id: currentUserId } } = useGetUserQuery()
  const { scopeType, scopeId } = useScope()
  const stateOwnerId = (currentUserId !== scopeId && scopeId !== 'me') && scopeType === 'user'
    ? scopeId : null

  // Modal state for single-use link
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { openSidebar: openSingleUseLinkSidebar } = useSingleUseLinkSidebar()

  const createSingleUseLink = useCallback((eventType) => {
    const eventTypeAttrs = getSingleUseLinkEventTypeAttrs(eventType)
    openSingleUseLinkSidebar({ selectedEventTypeAttrs: eventTypeAttrs, isCreation: 'true' })
  }, [openSingleUseLinkSidebar])

  const handleEventTypeSelect = useCallback((eventType) => {
    createSingleUseLink(eventType)
    setIsModalOpen(false)
  }, [createSingleUseLink])

  const handleModalCancel = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const openModal = useCallback(() => {
    toggle()
    setIsModalOpen(true)
  }, [toggle])

  const createButtonText = buttonLabel || I18n.t('publisher.action.create')

  return (
    <ButtonWrapper ref={setReference}>
      <Button
        size={isLargeMainContainer ? 'medium' : 'small'}
        onClick={toggle}
        id="scheduling-create-button"
        aria-label={I18n.t('publisher.action.create_new_event_type')}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={dropdownId}
      >
        <AddMiniIcon />
        <ButtonText>
          {createButtonText}
        </ButtonText>
        {isOpen ? <ArrowChevronUpMiniIcon /> : <ArrowChevronDownMiniIcon />}
      </Button>
      {isOpen && (
        <FloatingWrapper>
          <Dropdown>
            <Menu wide aria-labelledby="scheduling-create-button" dropdownOnRight={dropdownOnRight}>
              <UserEventTypeCreationDropdown
                useNewDesign
                stateOwnerId={stateOwnerId}
                toggleDropdown={toggle}
                source="blue create button right"
                openModal={openModal}
              />
            </Menu>
          </Dropdown>
        </FloatingWrapper>
      )}
      {isModalOpen && (
        <EventTypeSelectPopup
          isOpen={isModalOpen}
          onCancel={handleModalCancel}
          onEventTypeSelect={handleEventTypeSelect}
          onCreateNewEventType={toggle}
          defaultValue={null}
          actionKind="single_use_link_creation"
        />
      )}
    </ButtonWrapper>
  )
}
